import { useEffect, useState } from "react";
import { setDrawerShift, setManualOpenPrompt, setVoidAlertPrompt } from "./peripherals";
import { ManualOpenDialog } from "./peripherals/ui/ManualOpenDialog";
import { VoidAlertDialog } from "./peripherals/ui/VoidAlertDialog";
import { TicketFailureNotice } from "./peripherals/ui/TicketFailureNotice";
import type { FailureCode } from "./peripherals/drawer-module";

/**
 * The host side of the module contract.
 *
 * A module subscribes to core events and never gets called by core — but two
 * things it cannot know on its own have to come from the shell: which shift a
 * drawer event belongs to, and how to put a question in front of staff. Both
 * are exported as setters from the peripherals module and, until this file
 * existed, were called by nobody: drawer events would have been written with a
 * null shift, and the manual-open acknowledgement — the whole audit trail for
 * "the hardware was down and someone opened it with the key" — could never
 * appear, because `promptHost` was always null and the module correctly refuses
 * to invent an acknowledgement nobody gave.
 *
 * IT LIVES IN `src/modules/`, so the shell mounts one component and stays
 * ignorant of peripherals. Core imports none of this.
 */

interface PendingPrompt {
  failureCode: FailureCode;
  resolve: (acknowledged: boolean) => void;
}

interface PendingVoidAlert {
  orderNumber: string;
  resolve: () => void;
}

export function ModuleHost({ shiftId }: { shiftId: string | null }) {
  const [pending, setPending] = useState<PendingPrompt | null>(null);
  const [voidAlert, setVoidAlert] = useState<PendingVoidAlert | null>(null);

  // Attribution for anything the drawer records. Cleared on unmount so a
  // closed shift cannot collect an event that belongs to the next one.
  useEffect(() => {
    setDrawerShift(shiftId);
    return () => setDrawerShift(null);
  }, [shiftId]);

  /**
   * The module asks; this answers.
   *
   * The promise is deliberately left unresolved while the dialog is open — the
   * module awaits it, and the awaiting is what makes the manual/success row
   * land after a real person confirmed rather than beside the failure by
   * default. It cannot block the sale: by the time this renders the sale is
   * finalized and the drawer attempt already recorded (FR-102).
   */
  useEffect(() => {
    setManualOpenPrompt(
      (failureCode) =>
        new Promise<boolean>((resolve) => setPending({ failureCode, resolve })),
    );
    return () => setManualOpenPrompt(null);
  }, []);

  /**
   * The void/refund alert (specs/002-barista-order-tickets, FR-228).
   *
   * Same shape as the manual-open prompt above: the module awaits the
   * promise, which is what makes the ticket module's subscriber wait for a
   * real acknowledgement rather than moving on the instant the event fires.
   */
  useEffect(() => {
    setVoidAlertPrompt(
      (orderNumber) => new Promise<void>((resolve) => setVoidAlert({ orderNumber, resolve })),
    );
    return () => setVoidAlertPrompt(null);
  }, []);

  const answer = (acknowledged: boolean) => {
    pending?.resolve(acknowledged);
    setPending(null);
  };

  const acknowledgeVoidAlert = () => {
    voidAlert?.resolve();
    setVoidAlert(null);
  };

  return (
    <>
      {/* A banner, not an overlay — a failed ticket must never block the till
          (FR-212), unlike the two dialogs below. */}
      <TicketFailureNotice />

      {(pending || voidAlert) && (
        <div className="till__overlay">
          {pending && (
            <ManualOpenDialog
              failureCode={pending.failureCode}
              onAcknowledge={() => answer(true)}
              onDismiss={() => answer(false)}
            />
          )}
          {voidAlert && (
            <VoidAlertDialog
              orderNumber={voidAlert.orderNumber}
              onAcknowledge={acknowledgeVoidAlert}
            />
          )}
        </div>
      )}
    </>
  );
}

export default ModuleHost;
